// BatchJobsSection.jsx
// Manages the Batch Jobs table, all job state/logic,
// and all menus/drawers/dialogs specific to batch jobs.
import { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import AddIcon from "@mui/icons-material/Add";
import ArticleIcon from "@mui/icons-material/Article";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import DownloadIcon from "@mui/icons-material/Download";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import jsyaml from "js-yaml";

import {
  supabaseAuthenticatedApi,
  workspaceBackendUrl,
} from "../../../../components/SupabaseClient";
import { SideDrawer } from "../../../../components/SideDrawer";

import usePolling from "../../../../hooks/usePolling";

import SectionHeader from "../components/SectionHeader";
import ExperimentProfile from "../components/ExperimentProfile";
import {
  StyledListItemIcon,
  ProgressMessage,
  PodLogsView,
  ACTION_MENU_PROPS,
} from "../components/ViewUtils";

import JobFilters from "./JobFilters";
import {
  JOBS_CONTENT_MIN_WIDTH,
  JOBS_TOOLBAR_BUTTON_WIDTH,
} from "./jobDashboardLayout";
import { EMPTY_FILTERS, jobListPath, dateText } from "./jobFilters";
import { mutateJobs, mutationSucceeded } from "./jobMutations";
import BatchJobListTable, { TERMINAL_STATUSES } from "./BatchJobListTable";

const BatchJobsSection = ({ onJobSubmit }) => {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [day, setDay] = useState(() => dateText(new Date()));
  const filterRevision = useRef(0);
  const listPath = jobListPath(filters);
  const currentPath = useRef(listPath);
  currentPath.current = listPath;

  const [jobs, setJobs] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const jobsLoading = listLoading || detailsLoading;
  const [jobsError, setJobsError] = useState(null);
  const [loadedPath, setLoadedPath] = useState(null);
  const [inventoryRevision, setInventoryRevision] = useState(0);
  const [inventoryValid, setInventoryValid] = useState(false);
  const [totalCount, setTotalCount] = useState(undefined);

  const [selectedJobs, setSelectedJobs] = useState(new Map());
  const [displayedIds, setDisplayedIds] = useState([]);
  const visibleJobIds = useRef(null);
  const [expandedJobRows, setExpandedJobRows] = useState({});

  const [mutation, setMutation] = useState(null);
  const [mutationPending, setMutationPending] = useState(false);

  const [jobMenuState, setJobMenuState] = useState({
    anchorEl: null,
    jobId: null,
    jobName: null,
    isTerminal: false,
  });
  const [jobPodMenuState, setJobPodMenuState] = useState({
    anchorEl: null,
    jobId: null,
  });

  const [jobProfileDrawerState, setJobProfileDrawerState] = useState({
    open: false,
    experiment: null,
  });

  const [jobLogDrawerState, setJobLogDrawerState] = useState({
    open: false,
    jobId: null,
    jobName: null,
  });
  const [jobLogContent, setJobLogContent] = useState("");
  const [jobLogError, setJobLogError] = useState(null);
  const [jobLogLoading, setJobLogLoading] = useState(false);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const next = new Date();
    next.setHours(24, 0, 0, 0);
    const timer = setTimeout(
      () => setDay(dateText(new Date())),
      next.getTime() - Date.now(),
    );
    return () => clearTimeout(timer);
  }, [day]);

  const fetchJobs = async (isMounted) => {
    const revision = filterRevision.current;
    const isActive = () =>
      isMounted() &&
      currentPath.current === listPath &&
      filterRevision.current === revision;
    if (!isActive()) return;

    setListLoading(true);
    setJobsError(null);

    try {
      const listResp = await supabaseAuthenticatedApi.get(
        listPath,
        workspaceBackendUrl,
      );
      const list = await listResp.json();
      if (!isActive()) return;

      if (
        !Array.isArray(list.jobs) ||
        list.count !== list.jobs.length ||
        !Number.isInteger(list.total_count)
      ) {
        throw new Error("Incomplete job inventory. Please refresh.");
      }

      const inventory = list.jobs;
      setLoadedPath(listPath);
      setInventoryValid(true);
      setTotalCount(list.total_count);

      setSelectedJobs((previous) => {
        const next = new Map(previous);
        inventory.forEach((job) => {
          if (next.has(job.job_id)) next.set(job.job_id, job);
        });
        return next;
      });

      setJobs((previous) => {
        const cached = new Map(previous.map((job) => [job.job_id, job]));
        return inventory.map((info) => {
          const existing = cached.get(info.job_id);
          return {
            ...existing,
            ...info,
            job_name:
              info.job_name ||
              existing?.job_name ||
              info.job_label ||
              info.job_id,
          };
        });
      });

      setInventoryRevision((previous) => previous + 1);
    } catch (err) {
      if (isActive()) {
        setInventoryValid(false);
        setJobsError(err.message || "Failed to load Jobs.");
      }
    } finally {
      if (isActive()) setListLoading(false);
    }
  };

  const fetchJobDetails = async (isMounted) => {
    const revision = filterRevision.current;
    const pageIds = visibleJobIds.current;
    const pageIsCurrent = () =>
      isMounted() &&
      inventoryValid &&
      loadedPath === listPath &&
      currentPath.current === listPath &&
      filterRevision.current === revision &&
      visibleJobIds.current === pageIds;
    const pageJobs = jobs
      .filter((job) => pageIds?.includes(job.job_id))
      .slice(0, 10);
    if (!pageIsCurrent() || pageJobs.length === 0) return;

    setDetailsLoading(true);
    let details;
    try {
      const response = await supabaseAuthenticatedApi.post(
        "/jobs/status/batch",
        { job_ids: pageJobs.map((job) => job.job_id) },
        workspaceBackendUrl,
        10000,
      );
      const batch = await response.json();
      if (!pageIsCurrent()) return;
      const results = new Map(
        (batch.results || []).map((result) => [result.job_id, result]),
      );
      details = pageJobs.map(({ job_id }) => {
        const result = results.get(job_id);
        return result?.job?.job_id === job_id && !result.error
          ? { ...result.job, error: null, initialStatusLoaded: true }
          : {
              job_id,
              error: result?.error?.detail || "Job details unavailable.",
              initialStatusLoaded: true,
            };
      });
    } catch (err) {
      if (!pageIsCurrent()) return;
      details = pageJobs.map(({ job_id }) => ({
        job_id,
        error: err.message,
        initialStatusLoaded: true,
      }));
    } finally {
      if (isMounted()) setDetailsLoading(false);
    }
    if (!pageIsCurrent()) return;

    const byId = new Map(details.map((job) => [job.job_id, job]));
    setJobs((previous) =>
      previous.map((job) => ({
        ...job,
        ...byId.get(job.job_id),
        job_name: job.job_name,
        job_label: job.job_label,
        status: job.status,
        submitted_at: job.submitted_at,
      })),
    );
  };

  const refreshJobs = usePolling(fetchJobs, 30000);
  const refreshJobDetails = usePolling(fetchJobDetails, null);

  // Each successful inventory refresh also refreshes unchanged page IDs.
  useEffect(() => {
    if (inventoryRevision > 0) refreshJobDetails();
  }, [inventoryRevision, refreshJobDetails]);

  useEffect(() => {
    refreshJobs();
  }, [filters, listPath, refreshJobs]);

  const changeFilters = (next) => {
    // Invalidate in-flight responses immediately, before the next render.
    filterRevision.current += 1;
    currentPath.current = jobListPath(next);
    setFilters({ ...next });
  };

  // The ref guards in-flight page reads; state updates the visible selection count.
  const handleVisibleJobsChange = useCallback(
    (ids, refreshImmediately) => {
      const nextIds = ids.slice(0, 10);
      setDisplayedIds((previous) =>
        JSON.stringify(previous) === JSON.stringify(nextIds)
          ? previous
          : nextIds,
      );
      if (JSON.stringify(visibleJobIds.current) === JSON.stringify(nextIds)) {
        return;
      }
      visibleJobIds.current = nextIds;
      if (refreshImmediately) refreshJobDetails();
    },
    [refreshJobDetails],
  );

  const fetchJobLogs = useCallback(async (jobId) => {
    setJobLogLoading(true);
    setJobLogError(null);
    try {
      const resp = await supabaseAuthenticatedApi.get(
        `/jobs/logs/${jobId}`,
        workspaceBackendUrl,
      );
      const text = resp ? await resp.text() : "";
      setJobLogContent(text ?? "");
    } catch (err) {
      setJobLogError(err.message || "Failed to fetch job logs.");
    } finally {
      setJobLogLoading(false);
    }
  }, []);

  // Auto-refresh job logs when the drawer is open
  useEffect(() => {
    if (jobLogDrawerState.open && jobLogDrawerState.jobId) {
      fetchJobLogs(jobLogDrawerState.jobId);
      const id = setInterval(() => fetchJobLogs(jobLogDrawerState.jobId), 5000);
      return () => clearInterval(id);
    }
  }, [jobLogDrawerState.open, jobLogDrawerState.jobId, fetchJobLogs]);

  const handleJobActionMenuOpen = (event, jobId, jobName, jobStatus) =>
    setJobMenuState({
      anchorEl: event.currentTarget,
      jobId,
      jobName,
      isTerminal: TERMINAL_STATUSES.includes(jobStatus),
    });
  const handleJobActionMenuClose = () =>
    setJobMenuState((prev) => ({ ...prev, anchorEl: null }));

  const handleToggleJobExpand = (jobId) =>
    setExpandedJobRows((prev) => ({ ...prev, [jobId]: !prev[jobId] }));

  const handleJobPodActionMenuOpen = (event, _pod, jobId) =>
    setJobPodMenuState({ anchorEl: event.currentTarget, jobId });
  const handleJobPodActionMenuClose = () =>
    setJobPodMenuState({ anchorEl: null, jobId: null });

  const selectJobs = (items, checked) =>
    setSelectedJobs((previous) => {
      const next = new Map(previous);
      items.forEach((job) =>
        checked ? next.set(job.job_id, job) : next.delete(job.job_id),
      );
      return next;
    });

  const cancelTargets = [...selectedJobs.values()].filter(
    (job) =>
      !TERMINAL_STATUSES.includes(job.status) || job.status === "Cancelled",
  );
  const deleteTargets = [...selectedJobs.values()].filter((job) =>
    TERMINAL_STATUSES.includes(job.status),
  );
  const hiddenSelectedCount = [...selectedJobs.keys()].filter(
    (id) => !displayedIds.includes(id),
  ).length;
  const selectionDisabled =
    mutationPending ||
    jobsLoading ||
    !inventoryValid ||
    loadedPath !== listPath;

  const mutationResults = mutation?.results;
  const incompleteResults =
    mutationResults?.filter(
      (result) => !mutationSucceeded(result, mutation.action),
    ) ?? [];
  const partialCount = incompleteResults.filter(
    (result) => result.outcome === "partial",
  ).length;
  const succeededCount =
    (mutationResults?.length ?? 0) - incompleteResults.length;
  const failedCount = incompleteResults.length - partialCount;

  const openMutation = (action, targets, single = false) => {
    setMutation({
      action,
      targets: targets.map((job) => ({ ...job })),
      single,
      results: null,
    });
  };

  const handleOpenJobMutation = () => {
    openMutation(
      jobMenuState.isTerminal ? "delete_record" : "cancel",
      [{ job_id: jobMenuState.jobId, job_name: jobMenuState.jobName }],
      true,
    );
    handleJobActionMenuClose();
  };

  const confirmMutation = async () => {
    if (!mutation || mutationPending) return;

    setMutationPending(true);
    const results = await mutateJobs(
      mutation.targets.map((job) => job.job_id),
      mutation.action,
      mutation.single,
      () => mounted.current,
    );
    if (!mounted.current) return;

    const succeeded = new Set(
      results
        .filter((result) => mutationSucceeded(result, mutation.action))
        .map((result) => result.job_id),
    );
    setSelectedJobs(
      (previous) => new Map([...previous].filter(([id]) => !succeeded.has(id))),
    );
    if (mutation.action === "delete_record") {
      setJobs((previous) =>
        previous.filter((job) => !succeeded.has(job.job_id)),
      );
    }

    setMutation((current) => ({ ...current, results }));
    await refreshJobs();
    if (mounted.current) setMutationPending(false);
  };

  const handleRetryMutation = () => {
    const failedIds = new Set(incompleteResults.map((result) => result.job_id));
    setMutation({
      ...mutation,
      targets: mutation.targets.filter((job) => failedIds.has(job.job_id)),
      results: null,
    });
  };

  const handleViewJobProfile = () => {
    const job = jobs.find((j) => j.job_id === jobMenuState.jobId);
    if (job) {
      setJobProfileDrawerState({
        open: true,
        experiment: {
          workspace_id: job.job_id,
          status: {
            statefulset: {
              user_label: job.job_name,
              gpu_product: job.resources?.gpu_product ?? null,
              gpu_count: job.resources?.gpu_count ?? 0,
              spec_replicas: 1,
              mounted_datasets: (job.resources?.datasets ?? []).map((d) => [
                d.name,
                d.mount_path,
              ]),
            },
            pods: job.pods || [],
          },
        },
      });
    }
    handleJobActionMenuClose();
  };

  const handleSaveEFabricConfig = () => {
    const job = jobs.find((j) => j.job_id === jobMenuState.jobId);
    if (!job?.resources) return;
    const stripCr = (v) => {
      if (typeof v === "string") return v.replace(/\r/g, "");
      if (Array.isArray(v)) return v.map(stripCr);
      if (v !== null && typeof v === "object")
        return Object.fromEntries(
          Object.entries(v).map(([k, val]) => [k, stripCr(val)]),
        );
      return v;
    };
    const yaml = jsyaml.dump(stripCr(job.resources), { lineWidth: -1 });
    const blob = new Blob([yaml], { type: "text/yaml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "efabric.yml";
    a.click();
    URL.revokeObjectURL(url);
    handleJobActionMenuClose();
  };

  const handleOpenJobLogs = (jobId) => {
    const job = jobs.find((j) => j.job_id === jobId);
    setJobLogDrawerState({
      open: true,
      jobId,
      jobName: job?.job_name || jobId,
    });
  };

  const handleViewJobPodLogs = () => {
    if (jobPodMenuState.jobId) handleOpenJobLogs(jobPodMenuState.jobId);
    handleJobPodActionMenuClose();
  };

  return (
    <>
      {mutationPending && (
        <ProgressMessage>Applying job action…</ProgressMessage>
      )}

      {jobsError && (
        <Alert
          severity="error"
          sx={{ m: 2 }}
          onClose={() => setJobsError(null)}
        >
          {jobsError}
        </Alert>
      )}

      <SectionHeader
        icon={WorkOutlineIcon}
        title="Jobs"
        count={totalCount}
        subtitle="Non-interactive batch workloads that run to completion."
        action={
          <Button
            variant="solidButton"
            startIcon={<AddIcon />}
            onClick={onJobSubmit}
          >
            Submit a Job
          </Button>
        }
      />

      <Box
        role="region"
        aria-label="Jobs dashboard controls and table"
        tabIndex={0}
        sx={{ width: "100%", minWidth: 0, overflowX: "auto" }}
      >
        <Box sx={{ width: "100%", minWidth: JOBS_CONTENT_MIN_WIDTH }}>
          <JobFilters filters={filters} onChange={changeFilters} />
          <BatchJobListTable
            jobs={loadedPath === listPath ? jobs : []}
            filterKey={listPath}
            selectedJobs={selectedJobs}
            onSelectJobs={selectJobs}
            selectionDisabled={selectionDisabled}
            selectionControls={
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  flexWrap: "nowrap",
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                  alignItems: "center",
                }}
              >
                <Button
                  sx={{ width: JOBS_TOOLBAR_BUTTON_WIDTH, flexShrink: 0 }}
                  disabled={
                    selectedJobs.size
                      ? mutationPending
                      : selectionDisabled || !jobs.length
                  }
                  onClick={() =>
                    selectedJobs.size
                      ? setSelectedJobs(new Map())
                      : selectJobs(jobs, true)
                  }
                >
                  {selectedJobs.size ? "Clear all" : "Select all"}
                </Button>
                <Typography role="status" variant="body2">
                  {selectedJobs.size} selected · {hiddenSelectedCount} outside
                  this page
                </Typography>
                <Button
                  disabled={mutationPending || !cancelTargets.length}
                  onClick={() => openMutation("cancel", cancelTargets)}
                >
                  Cancel selected ({cancelTargets.length})
                </Button>
                <Button
                  color="error"
                  disabled={mutationPending || !deleteTargets.length}
                  onClick={() => openMutation("delete_record", deleteTargets)}
                >
                  Delete records ({deleteTargets.length})
                </Button>
              </Box>
            }
            loading={jobsLoading}
            expandedRows={expandedJobRows}
            onToggleExpand={handleToggleJobExpand}
            onActionMenuOpen={handleJobActionMenuOpen}
            onJobPodActionMenuOpen={handleJobPodActionMenuOpen}
            onViewJobLogs={handleOpenJobLogs}
            onVisibleJobsChange={handleVisibleJobsChange}
          />
        </Box>
      </Box>

      {/* Job action menu */}
      <Menu
        id="job-action-menu"
        anchorEl={jobMenuState.anchorEl}
        open={Boolean(jobMenuState.anchorEl)}
        onClose={handleJobActionMenuClose}
        {...ACTION_MENU_PROPS}
      >
        <MenuItem onClick={handleViewJobProfile}>
          <StyledListItemIcon>
            <InfoIcon fontSize="small" />
          </StyledListItemIcon>
          View Profile
        </MenuItem>
        <MenuItem onClick={handleSaveEFabricConfig}>
          <StyledListItemIcon>
            <DownloadIcon fontSize="small" />
          </StyledListItemIcon>
          Save EFabric Configuration
        </MenuItem>
        <MenuItem
          disabled={mutationPending}
          onClick={handleOpenJobMutation}
          className="destructive"
        >
          <StyledListItemIcon>
            {jobMenuState.isTerminal ? (
              <DeleteForeverIcon fontSize="small" />
            ) : (
              <DeleteIcon fontSize="small" />
            )}
          </StyledListItemIcon>
          {jobMenuState.isTerminal ? "Delete record" : "Cancel job"}
        </MenuItem>
      </Menu>

      {/* Job pod action menu */}
      <Menu
        id="job-pod-action-menu"
        anchorEl={jobPodMenuState.anchorEl}
        open={Boolean(jobPodMenuState.anchorEl)}
        onClose={handleJobPodActionMenuClose}
        {...ACTION_MENU_PROPS}
      >
        <MenuItem onClick={handleViewJobPodLogs}>
          <StyledListItemIcon>
            <ArticleIcon fontSize="small" />
          </StyledListItemIcon>
          View Logs
        </MenuItem>
      </Menu>

      <Dialog
        open={Boolean(mutation)}
        onClose={() => {
          if (!mutationPending) setMutation(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {mutation?.action === "cancel" ? "Cancel jobs" : "Delete job records"}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {mutation?.action === "cancel"
              ? "Stop these jobs and retain their history. Jobs that finish before cancellation will keep their records."
              : "Permanently delete these terminal job records and their stored logs. Active jobs will not be deleted."}
          </Typography>
          <Box component="ul" sx={{ maxHeight: 260, overflow: "auto", pl: 3 }}>
            {mutation?.targets.map((job) => (
              <li key={job.job_id}>
                {job.job_name || job.job_id}
                <Typography variant="caption" display="block">
                  {job.job_id}
                </Typography>
              </li>
            ))}
          </Box>
          {mutationResults && (
            <Box role="status">
              <Typography>
                {succeededCount} succeeded; {partialCount} partial;{" "}
                {failedCount} failed.
              </Typography>
              {incompleteResults.map((result) => (
                <Alert severity="warning" key={result.job_id} sx={{ mt: 1 }}>
                  {result.job_id}:{" "}
                  {(result.errors || []).join("; ") ||
                    result.code ||
                    "Action incomplete."}
                </Alert>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={mutationPending} onClick={() => setMutation(null)}>
            Close
          </Button>
          {!mutationResults && (
            <Button
              disabled={mutationPending}
              color="error"
              onClick={confirmMutation}
            >
              Confirm{" "}
              {mutation?.action === "cancel" ? "cancellation" : "deletion"}
            </Button>
          )}
          {incompleteResults.length > 0 && (
            <Button disabled={mutationPending} onClick={handleRetryMutation}>
              Review retry with same action
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Drawer: Job Profile */}
      <SideDrawer
        open={jobProfileDrawerState.open}
        onClose={() =>
          setJobProfileDrawerState({ open: false, experiment: null })
        }
        title={`Job Profile (${jobProfileDrawerState.experiment?.status?.statefulset?.user_label || "..."})`}
      >
        <ExperimentProfile experiment={jobProfileDrawerState.experiment} />
      </SideDrawer>

      {/* Drawer: Job Logs */}
      <SideDrawer
        open={jobLogDrawerState.open}
        onClose={() => {
          setJobLogDrawerState({ open: false, jobId: null, jobName: null });
          setJobLogContent("");
          setJobLogError(null);
          setJobLogLoading(false);
        }}
        title={`Job Logs (${jobLogDrawerState.jobName || jobLogDrawerState.jobId || "..."})`}
      >
        <PodLogsView
          logContent={jobLogContent}
          logError={jobLogError}
          logLoading={jobLogLoading}
        />
      </SideDrawer>
    </>
  );
};

BatchJobsSection.propTypes = {
  onJobSubmit: PropTypes.func,
};

export default BatchJobsSection;
